'use server';

import bcrypt from 'bcrypt';
import { revalidatePath } from 'next/cache';

import { getEffectiveUserId } from '@/lib/actions/impersonation';
import { logAuditEvent } from '@/lib/audit-log';
import { getSession } from '@/lib/auth-session';
import { authorize, getCapabilitySummary, invalidateCapabilityCache, invalidateGrantCache } from '@/lib/authz/authorize';
import { type PendingRoleInput, validatePendingRoleAssignments } from '@/lib/authz/pending-role-assignments';
import { CacheInvalidation } from '@/lib/cache';
import { prisma } from '@/lib/db';
import { sendEmail } from '@/lib/email';
import { logger } from '@/lib/errors';
import { type ActionResult, failure, ok } from '@/lib/types/action-result';
import { resolveSessionUserId } from '@/lib/auth';

import {
    buildDisplayName,
    buildUsernameBase,
    createEmailVerificationToken,
    findAvailableUsername,
    getActiveScopeGrantsForUser,
    getCurrentUserId,
    getMissingSmtpConfig,
    getRequestIp,
    isSmtpConfigured,
    MIN_PASSWORD_LENGTH,
    registerIpLimiter,
    safeLogSystemError,
    SALT_ROUNDS,
    suggestUsernameIpLimiter,
    verifyRecaptcha,
} from '../users-shared';

export async function suggestUsername(
    firstName: string,
    lastName: string,
): Promise<ActionResult<{ suggestion: string }>> {
    // Code-review S-A (2026-05-13): the iteration counter exposed by
    // `findAvailableUsername` (`john.doe.4`) confirms how many users
    // share the canonical name → username-enumeration oracle. Bound
    // by IP so an unauthenticated attacker can't enumerate.
    const ip = await getRequestIp();
    const { success } = await suggestUsernameIpLimiter.limit(`suggest-username-ip:${ip}`);
    if (!success) {
        return ok({ suggestion: '' });
    }
    const base = buildUsernameBase(firstName, lastName);
    if (!base) return ok({ suggestion: '' });
    const suggestion = await findAvailableUsername(base);
    return ok({ suggestion });
}

export async function createUser(data: { firstName: string; lastName: string; email: string; username?: string; password?: string; emailVerified?: boolean }): Promise<ActionResult> {
    try {
        const session = await getSession();
        const sessionUserId = resolveSessionUserId(session);
        const effectiveUserId = (await getEffectiveUserId()) ?? sessionUserId;
        if (!effectiveUserId) return failure('Nicht authentifiziert');
        const decision = await authorize({
            userId: effectiveUserId,
            action: 'portal.manage',
            resource: { type: 'portal' },
        });
        if (!decision.allowed) return failure('Keine Berechtigung');

        const name = buildDisplayName(data.firstName, data.lastName);
        if (!name) return failure('Vorname und Nachname sind erforderlich');

        const usernameInput = data.username?.trim() ?? '';
        const username = usernameInput || await findAvailableUsername(buildUsernameBase(data.firstName, data.lastName));
        if (!username) return failure('Benutzername ist erforderlich');

        const existingUsername = await prisma.user.findUnique({ where: { username } });
        if (existingUsername) return failure('Benutzername ist bereits vergeben');

        const hashedPassword = data.password ? await bcrypt.hash(data.password, SALT_ROUNDS) : undefined;

        const shouldVerifyEmail = data.emailVerified ?? true;
        const newUser = await prisma.user.create({
            data: {
                name,
                firstName: data.firstName.trim(),
                lastName: data.lastName.trim(),
                email: data.email,
                password: hashedPassword,
                username,
                emailVerified: shouldVerifyEmail ? new Date() : null,
                creationSource: 'admin',
                createdById: effectiveUserId,
            }
        });

        // Create credential account entry for Better Auth
        if (hashedPassword) {
            await prisma.account.create({
                data: {
                    userId: newUser.id,
                    accountId: String(newUser.id),
                    providerId: 'credential',
                    provider: 'credential',
                    providerAccountId: String(newUser.id),
                    type: 'credential',
                    password: hashedPassword,
                },
            });
        }

        if (newUser.email && !shouldVerifyEmail) {
            const token = await createEmailVerificationToken(newUser.id);
            const baseUrl = process.env.PORTAL_PUBLIC_URL || process.env.NEXTAUTH_URL || '';
            const verificationUrl = `${baseUrl.replace(/\/$/, '')}/verify-email?token=${token}`;
            const emailResult = await sendEmail({
                to: newUser.email,
                subject: 'Bitte E-Mail-Adresse bestaetigen',
                text: `Bitte bestaetigen Sie Ihre E-Mail-Adresse: ${verificationUrl}`,
                html: `<p>Bitte bestaetigen Sie Ihre E-Mail-Adresse:</p><p><a href=\"${verificationUrl}\">${verificationUrl}</a></p>`,
            });
            if (emailResult.error) {
                return failure(emailResult.error);
            }
        }

        revalidatePath('/admin/users', 'layout');

        if (session?.user?.id) {
            await logAuditEvent({
                userId: resolveSessionUserId(session)!,
                action: 'user.create',
                resourceType: 'User',
                resourceId: newUser.id,
                newValue: { name: newUser.name, firstName: newUser.firstName, lastName: newUser.lastName, email: newUser.email, username: newUser.username },
            });
        }

        return ok();
    } catch (e) {
        console.error(e);
        void safeLogSystemError('User create failed', e, { email: data.email, username: data.username });
        return failure('Fehler beim Erstellen des Benutzers');
    }
}

export async function createUserAsNetworkAdmin(data: {
    firstName: string;
    lastName: string;
    email: string;
    username?: string;
    password?: string;
    emailVerified?: boolean;
    /**
     * Network the new user should be added to as `net_user`. Required when
     * the calling admin manages more than one network — without this the
     * UI must present a picker. Optional (auto-resolved) when the admin
     * manages exactly one network.
     */
    networkId?: number;
}): Promise<ActionResult<{ userId: number }>> {
    try {
        const session = await getSession();
        const sessionUserId = resolveSessionUserId(session);
        const effectiveUserId = (await getEffectiveUserId()) ?? sessionUserId;
        if (!effectiveUserId) return failure('Nicht authentifiziert');

        const caps = await getCapabilitySummary(effectiveUserId);
        if (!caps.isNetworkAdmin && !caps.isAdmin) {
            return failure('Keine Berechtigung');
        }

        // Resolve which networks this admin can grant net_user on.
        // Portal admins can target any network; network admins are limited
        // to networks where their binding has the `network:manage` scope.
        const adminGrants = await getActiveScopeGrantsForUser(effectiveUserId);
        const managedNetworkIds = caps.isAdmin
            ? null // unrestricted
            : Array.from(new Set(
                adminGrants
                    .filter((g) => g.networkId && !g.plantMemberNr && g.scopes.includes('network:manage'))
                    .map((g) => g.networkId as number),
            ));

        // Decide which network the new user gets net_user on.
        // - networkId provided → use it (after verifying the admin manages it)
        // - exactly one managed network → auto-pick it
        // - portal admin without networkId → no auto network, user is created
        //   with no network binding (admin can add later via UI)
        // - multiple managed networks without networkId → error, UI must pick
        let resolvedNetworkId: number | null = null;
        if (typeof data.networkId === 'number') {
            if (managedNetworkIds && !managedNetworkIds.includes(data.networkId)) {
                return failure('Keine Berechtigung für dieses Netz');
            }
            resolvedNetworkId = data.networkId;
        } else if (managedNetworkIds && managedNetworkIds.length === 1) {
            resolvedNetworkId = managedNetworkIds[0];
        } else if (managedNetworkIds && managedNetworkIds.length > 1) {
            return failure('Bitte ein Netz auswählen');
        }

        const email = data.email.trim().toLowerCase();
        const name = buildDisplayName(data.firstName, data.lastName);
        if (!name) return failure('Vorname und Nachname sind erforderlich');

        const existingEmail = await prisma.user.findUnique({ where: { email } });
        if (existingEmail) return failure('E-Mail wird bereits verwendet');

        const usernameInput = data.username?.trim() ?? '';
        const username = usernameInput || await findAvailableUsername(buildUsernameBase(data.firstName, data.lastName));
        if (!username) return failure('Benutzername ist erforderlich');

        const existingUsername = await prisma.user.findUnique({ where: { username } });
        if (existingUsername) return failure('Benutzername ist bereits vergeben');

        const hashedPassword = data.password ? await bcrypt.hash(data.password, SALT_ROUNDS) : undefined;
        // Default to UNVERIFIED so the new user must confirm their email.
        // The previous default (`?? true`) silently auto-verified everyone
        // a Netz-Admin created, which both skipped the confirmation flow
        // (no email sent) and circumvented the verification gate that
        // protects against typos / wrong addresses landing in the user table.
        const shouldVerifyEmail = data.emailVerified ?? false;

        const newUser = await prisma.user.create({
            data: {
                name,
                firstName: data.firstName.trim(),
                lastName: data.lastName.trim(),
                email,
                password: hashedPassword,
                username,
                emailVerified: shouldVerifyEmail ? new Date() : null,
                creationSource: 'network_admin',
                createdById: effectiveUserId,
            }
        });

        // Create credential account entry for Better Auth
        if (hashedPassword) {
            await prisma.account.create({
                data: {
                    userId: newUser.id,
                    accountId: String(newUser.id),
                    providerId: 'credential',
                    provider: 'credential',
                    providerAccountId: String(newUser.id),
                    type: 'credential',
                    password: hashedPassword,
                },
            });
        }

        // Assign net_user role on the resolved network. We treat a failure
        // here as fatal (instead of swallowing as before) — silently dropping
        // the role binding is exactly the bug Thomas Handler hit, where a
        // newly-created user got no network access and disappeared from the
        // admin's user list.
        if (resolvedNetworkId !== null) {
            const netUserRole = await prisma.role.findUnique({ where: { keyName: 'net_user' } });
            if (!netUserRole) {
                void safeLogSystemError('net_user role missing — cannot bind newly-created user', undefined, { userId: newUser.id });
                return failure('Rolle "net_user" nicht gefunden — bitte Administrator informieren.');
            }
            try {
                await prisma.accessBinding.create({
                    data: {
                        userId: newUser.id,
                        roleId: netUserRole.id,
                        networkId: resolvedNetworkId,
                        plantMemberNr: null,
                        createdById: effectiveUserId,
                        validFrom: new Date(),
                        notes: JSON.stringify({ createdByNetworkAdmin: true }),
                    },
                });
            } catch (bindingError) {
                void safeLogSystemError('Failed to attach net_user binding', bindingError, {
                    userId: newUser.id,
                    networkId: resolvedNetworkId,
                });
                return failure('Benutzer wurde angelegt, aber die Netz-Zuweisung ist fehlgeschlagen. Bitte erneut zuweisen.');
            }
        }

        if (newUser.email && !shouldVerifyEmail) {
            const token = await createEmailVerificationToken(newUser.id);
            const baseUrl = process.env.PORTAL_PUBLIC_URL || process.env.NEXTAUTH_URL || '';
            const verificationUrl = `${baseUrl.replace(/\/$/, '')}/verify-email?token=${token}`;
            await sendEmail({
                to: newUser.email,
                subject: 'Bitte E-Mail-Adresse bestaetigen',
                text: `Bitte bestaetigen Sie Ihre E-Mail-Adresse: ${verificationUrl}`,
                html: `<p>Bitte bestaetigen Sie Ihre E-Mail-Adresse:</p><p><a href="${verificationUrl}">${verificationUrl}</a></p>`,
            });
        }

        revalidatePath('/admin/users', 'layout');

        if (session?.user?.id) {
            await logAuditEvent({
                userId: resolveSessionUserId(session)!,
                action: 'user.create',
                resourceType: 'User',
                resourceId: newUser.id,
                newValue: {
                    name: newUser.name,
                    email: newUser.email,
                    username: newUser.username,
                    creationSource: 'network_admin',
                },
            });
        }

        return ok({ userId: newUser.id });
    } catch (e) {
        console.error(e);
        void safeLogSystemError('Network admin user create failed', e, { email: data.email });
        return failure('Fehler beim Erstellen des Benutzers');
    }
}

export async function updateUserAdmin(data: { id: number; firstName: string; lastName: string; email: string; username: string; password?: string; emailVerified?: boolean; expectedUpdatedAt?: string | Date | null }): Promise<ActionResult> {
    try {
        const session = await getSession();
        const sessionUserId = resolveSessionUserId(session);
        const effectiveUserId = (await getEffectiveUserId()) ?? sessionUserId;
        if (!effectiveUserId) return failure('Nicht authentifiziert');
        const decision = await authorize({
            userId: effectiveUserId,
            action: 'portal.manage',
            resource: { type: 'portal' },
        });
        if (!decision.allowed) return failure('Keine Berechtigung');

        const targetUser = await prisma.user.findUnique({ where: { id: data.id } });
        if (!targetUser) return failure('Benutzer nicht gefunden');

        // Codex M2 (2026-05-11): optimistic locking. Caller may pass the
        // `updatedAt` it saw when opening the edit form; if it doesn't
        // match anymore another admin has modified the user in the
        // meantime and our write would silently clobber their changes.
        // Opt-in to keep older callers working — when the caller omits
        // `expectedUpdatedAt` we fall back to last-write-wins behaviour.
        if (data.expectedUpdatedAt !== undefined && data.expectedUpdatedAt !== null) {
            const expected = data.expectedUpdatedAt instanceof Date
                ? data.expectedUpdatedAt
                : new Date(data.expectedUpdatedAt);
            if (Number.isFinite(expected.getTime())
                && targetUser.updatedAt.getTime() !== expected.getTime()) {
                return failure('Die Daten wurden zwischenzeitlich von einer anderen Sitzung geändert. Bitte laden Sie die Seite neu und versuchen Sie es erneut.');
            }
        }

        const username = data.username.trim();
        if (!username) return failure('Benutzername ist erforderlich');

        const name = buildDisplayName(data.firstName, data.lastName);
        if (!name) return failure('Vorname und Nachname sind erforderlich');

        if (username !== targetUser.username) {
            const existingUsername = await prisma.user.findUnique({ where: { username } });
            if (existingUsername) return failure('Benutzername ist bereits vergeben');
        }

        let emailChanged = false;
        const shouldVerifyEmail = data.emailVerified ?? Boolean(targetUser.emailVerified);

        if (data.email !== targetUser.email) {
            const existing = await prisma.user.findUnique({ where: { email: data.email } });
            if (existing) return failure('Email wird bereits verwendet');
            emailChanged = true;
        }

        const updateData: { name: string; firstName: string; lastName: string; email: string; username: string; emailVerified?: Date | null; password?: string } = {
            name,
            firstName: data.firstName.trim(),
            lastName: data.lastName.trim(),
            email: data.email,
            username,
            emailVerified: shouldVerifyEmail ? (emailChanged ? new Date() : targetUser.emailVerified ?? new Date()) : null,
        };

        if (data.password) {
            updateData.password = await bcrypt.hash(data.password, SALT_ROUNDS);
        }

        // When expectedUpdatedAt is provided we also gate the WRITE with the
        // same field so a concurrent update slipping in between the SELECT
        // above and the UPDATE here cannot pass the check.
        if (data.expectedUpdatedAt !== undefined && data.expectedUpdatedAt !== null) {
            const expected = data.expectedUpdatedAt instanceof Date
                ? data.expectedUpdatedAt
                : new Date(data.expectedUpdatedAt);
            if (Number.isFinite(expected.getTime())) {
                const guardedUpdate = await prisma.user.updateMany({
                    where: { id: data.id, updatedAt: expected },
                    data: updateData,
                });
                if (guardedUpdate.count !== 1) {
                    return failure('Die Daten wurden zwischenzeitlich von einer anderen Sitzung geändert. Bitte laden Sie die Seite neu und versuchen Sie es erneut.');
                }
            } else {
                await prisma.user.update({ where: { id: data.id }, data: updateData });
            }
        } else {
            await prisma.user.update({ where: { id: data.id }, data: updateData });
        }

        const updatedUser = await prisma.user.findUniqueOrThrow({ where: { id: data.id } });

        if (updatedUser.email && !shouldVerifyEmail) {
            const token = await createEmailVerificationToken(updatedUser.id);
            const baseUrl = process.env.PORTAL_PUBLIC_URL || process.env.NEXTAUTH_URL || '';
            const verificationUrl = `${baseUrl.replace(/\/$/, '')}/verify-email?token=${token}`;
            const emailResult = await sendEmail({
                to: updatedUser.email,
                subject: 'Bitte E-Mail-Adresse bestaetigen',
                text: `Bitte bestaetigen Sie Ihre E-Mail-Adresse: ${verificationUrl}`,
                html: `<p>Bitte bestaetigen Sie Ihre E-Mail-Adresse:</p><p><a href=\"${verificationUrl}\">${verificationUrl}</a></p>`,
            });
            if (emailResult.error) {
                return failure(emailResult.error);
            }
        }


        revalidatePath('/admin/users', 'layout');

        if (session?.user?.id) {
            await logAuditEvent({
                userId: resolveSessionUserId(session)!,
                action: 'user.update',
                resourceType: 'User',
                resourceId: data.id,
                oldValue: { name: targetUser.name, firstName: targetUser.firstName, lastName: targetUser.lastName, email: targetUser.email, username: targetUser.username, emailVerified: targetUser.emailVerified },
                newValue: { name: updateData.name, firstName: updateData.firstName, lastName: updateData.lastName, email: updateData.email, username: updateData.username, emailVerified: updateData.emailVerified },
            });
        }

        return ok();
    } catch (e) {
        console.error(e);
        void safeLogSystemError('User update failed', e, { userId: data.id });
        return failure('Fehler beim Aktualisieren des Benutzers');
    }
}

export async function deleteUser(id: number): Promise<ActionResult> {
    try {
        const session = await getSession();
        const sessionUserId = resolveSessionUserId(session);
        const effectiveUserId = (await getEffectiveUserId()) ?? sessionUserId;
        if (!effectiveUserId) return failure('Nicht authentifiziert');
        const decision = await authorize({
            userId: effectiveUserId,
            action: 'portal.manage',
            resource: { type: 'portal' },
        });
        if (!decision.allowed) return failure('Keine Berechtigung');

        if (id === effectiveUserId) return failure('Sie können sich nicht selbst löschen');

        const targetUser = await prisma.user.findUnique({ where: { id } });
        if (!targetUser) return failure('Benutzer nicht gefunden');
        if (targetUser.deletedAt) return failure('Benutzer ist bereits gelöscht');

        await prisma.$transaction([
            prisma.user.update({
                where: { id },
                data: {
                    deletedAt: new Date(),
                    deletedById: effectiveUserId,
                    deletedEmail: targetUser.email,
                    deletedUsername: targetUser.username,
                    email: null,
                    username: null,
                    externalId: null,
                    pendingEmail: null,
                },
            }),
            prisma.accessBinding.deleteMany({ where: { userId: id } }),
            prisma.groupMembership.deleteMany({ where: { userId: id } }),
            prisma.report.deleteMany({ where: { userId: id } }),
            prisma.session.deleteMany({ where: { userId: id } }),
            prisma.account.deleteMany({ where: { userId: id } }),
            prisma.passkey.deleteMany({ where: { userId: id } }),
            prisma.emailVerificationToken.deleteMany({ where: { userId: id } }),
            prisma.passwordResetToken.deleteMany({ where: { userId: id } }),
        ]);

        // Clear all caches so stale permissions don't leak
        invalidateGrantCache(id);
        void invalidateCapabilityCache(id);
        await CacheInvalidation.user(id);

        if (session?.user?.id) {
            await logAuditEvent({
                userId: resolveSessionUserId(session)!,
                action: 'user.soft_delete',
                resourceType: 'User',
                resourceId: id,
                metadata: {
                    deletedUser: {
                        name: targetUser.name,
                        email: targetUser.email,
                        username: targetUser.username,
                    },
                },
            });
        }

        revalidatePath('/admin/users', 'layout');
        return ok();
    } catch (e) {
        console.error(e);
        void safeLogSystemError('User soft-delete failed', e, { userId: id });
        return failure('Fehler beim Löschen des Benutzers');
    }
}

export async function suspendUser(id: number): Promise<ActionResult> {
    try {
        const session = await getSession();
        const sessionUserId = resolveSessionUserId(session);
        const effectiveUserId = (await getEffectiveUserId()) ?? sessionUserId;
        if (!effectiveUserId) return failure('Nicht authentifiziert');

        // Only portal admins may suspend users
        const decision = await authorize({
            userId: effectiveUserId,
            action: 'portal.manage',
            resource: { type: 'portal' },
        });
        if (!decision.allowed) return failure('Nur Portal-Administratoren dürfen Benutzer deaktivieren');

        if (id === effectiveUserId) return failure('Sie können sich nicht selbst deaktivieren');

        const targetUser = await prisma.user.findUnique({ where: { id } });
        if (!targetUser) return failure('Benutzer nicht gefunden');
        if (targetUser.deletedAt) return failure('Benutzer ist gelöscht');
        if (targetUser.suspendedAt) return failure('Benutzer ist bereits deaktiviert');

        await prisma.$transaction([
            prisma.user.update({
                where: { id },
                data: {
                    suspendedAt: new Date(),
                    suspendedById: effectiveUserId,
                },
            }),
            // Sessions of a suspended user must die immediately. Better-Auth
            // does not re-check `suspendedAt` on each getSession() call, so a
            // stale cookie would otherwise keep working until expiry (default
            // 7 days). Mirrors the deleteUser pattern.
            prisma.session.deleteMany({ where: { userId: id } }),
        ]);

        // Clear caches so suspended user loses access immediately
        invalidateGrantCache(id);
        void invalidateCapabilityCache(id);
        await CacheInvalidation.user(id);

        await logAuditEvent({
            userId: effectiveUserId,
            action: 'user.suspend',
            resourceType: 'User',
            resourceId: id,
            metadata: {
                suspendedUser: {
                    name: targetUser.name,
                    email: targetUser.email,
                    username: targetUser.username,
                },
            },
        });

        revalidatePath('/admin/users', 'layout');
        return ok();
    } catch (e) {
        console.error(e);
        void safeLogSystemError('User suspend failed', e, { userId: id });
        return failure('Fehler beim Deaktivieren des Benutzers');
    }
}

export async function unsuspendUser(id: number): Promise<ActionResult> {
    try {
        const session = await getSession();
        const sessionUserId = resolveSessionUserId(session);
        const effectiveUserId = (await getEffectiveUserId()) ?? sessionUserId;
        if (!effectiveUserId) return failure('Nicht authentifiziert');

        // Only portal admins may unsuspend users
        const portalDecision = await authorize({
            userId: effectiveUserId,
            action: 'portal.manage',
            resource: { type: 'portal' },
        });
        if (!portalDecision.allowed) return failure('Nur Portal-Administratoren dürfen Benutzer aktivieren');

        const targetUser = await prisma.user.findUnique({ where: { id } });
        if (!targetUser) return failure('Benutzer nicht gefunden');
        if (!targetUser.suspendedAt) return failure('Benutzer ist nicht deaktiviert');

        await prisma.user.update({
            where: { id },
            data: {
                suspendedAt: null,
                suspendedById: null,
            },
        });

        await logAuditEvent({
            userId: effectiveUserId,
            action: 'user.unsuspend',
            resourceType: 'User',
            resourceId: id,
            metadata: {
                unsuspendedUser: {
                    name: targetUser.name,
                    email: targetUser.email,
                    username: targetUser.username,
                },
            },
        });

        revalidatePath('/admin/users', 'layout');
        return ok();
    } catch (e) {
        console.error(e);
        void safeLogSystemError('User unsuspend failed', e, { userId: id });
        return failure('Fehler beim Aktivieren des Benutzers');
    }
}

export async function restoreUser(id: number, options?: { restorePermissions?: boolean }): Promise<ActionResult> {
    try {
        const session = await getSession();
        const sessionUserId = resolveSessionUserId(session);
        const effectiveUserId = (await getEffectiveUserId()) ?? sessionUserId;
        if (!effectiveUserId) return failure('Nicht authentifiziert');
        const decision = await authorize({
            userId: effectiveUserId,
            action: 'portal.manage',
            resource: { type: 'portal' },
        });
        if (!decision.allowed) return failure('Keine Berechtigung');

        const targetUser = await prisma.user.findUnique({ where: { id } });
        if (!targetUser) return failure('Benutzer nicht gefunden');
        if (!targetUser.deletedAt) return failure('Benutzer ist nicht gelöscht');

        if (targetUser.deletedEmail) {
            const emailConflict = await prisma.user.findUnique({ where: { email: targetUser.deletedEmail } });
            if (emailConflict) return failure('Die E-Mail-Adresse wird bereits von einem anderen Benutzer verwendet');
        }

        if (targetUser.deletedUsername) {
            const usernameConflict = await prisma.user.findUnique({ where: { username: targetUser.deletedUsername } });
            if (usernameConflict) return failure('Der Benutzername wird bereits von einem anderen Benutzer verwendet');
        }

        const restorePermissions = options?.restorePermissions ?? true;

        await prisma.$transaction(async (tx) => {
            await tx.user.update({
                where: { id },
                data: {
                    deletedAt: null,
                    deletedById: null,
                    email: targetUser.deletedEmail,
                    username: targetUser.deletedUsername,
                    deletedEmail: null,
                    deletedUsername: null,
                },
            });

            if (!restorePermissions) {
                await tx.accessBinding.deleteMany({ where: { userId: id } });
                await tx.groupMembership.deleteMany({ where: { userId: id } });
                await tx.report.deleteMany({ where: { userId: id } });
            }
        });

        if (session?.user?.id) {
            await logAuditEvent({
                userId: resolveSessionUserId(session)!,
                action: 'user.restore',
                resourceType: 'User',
                resourceId: id,
                metadata: {
                    restoredUser: {
                        name: targetUser.name,
                        email: targetUser.deletedEmail,
                        username: targetUser.deletedUsername,
                    },
                    restorePermissions,
                },
            });
        }

        revalidatePath('/admin/users', 'layout');
        return ok();
    } catch (e) {
        console.error(e);
        void safeLogSystemError('User restore failed', e, { userId: id });
        return failure('Fehler beim Wiederherstellen des Benutzers');
    }
}

export async function purgeUser(id: number): Promise<ActionResult> {
    try {
        const session = await getSession();
        const sessionUserId = resolveSessionUserId(session);
        const effectiveUserId = (await getEffectiveUserId()) ?? sessionUserId;
        if (!effectiveUserId) return failure('Nicht authentifiziert');
        const decision = await authorize({
            userId: effectiveUserId,
            action: 'portal.manage',
            resource: { type: 'portal' },
        });
        if (!decision.allowed) return failure('Keine Berechtigung');

        const targetUser = await prisma.user.findUnique({ where: { id } });
        if (!targetUser) return failure('Benutzer nicht gefunden');
        if (!targetUser.deletedAt) return failure('Nur gelöschte Benutzer können endgültig entfernt werden');

        await prisma.user.delete({ where: { id } });

        invalidateGrantCache(id);
        void invalidateCapabilityCache(id);
        await CacheInvalidation.user(id);

        if (session?.user?.id) {
            await logAuditEvent({
                userId: resolveSessionUserId(session)!,
                action: 'user.purge',
                resourceType: 'User',
                resourceId: id,
                metadata: {
                    purgedUser: {
                        name: targetUser.name,
                        deletedEmail: targetUser.deletedEmail,
                        deletedUsername: targetUser.deletedUsername,
                    },
                },
            });
        }

        revalidatePath('/admin/users', 'layout');
        return ok();
    } catch (e) {
        console.error(e);
        void safeLogSystemError('User purge failed', e, { userId: id });
        return failure('Fehler beim endgültigen Löschen des Benutzers');
    }
}

export async function updateProfile(data: { firstName: string; lastName: string; email: string; username: string }): Promise<ActionResult<{ emailVerified: Date | null; pendingEmail: string | null }>> {
    try {
        const userId = await getCurrentUserId();
        if (!userId) return failure('Nicht eingeloggt');

        const currentUser = await prisma.user.findUnique({ where: { id: userId } });
        if (!currentUser) return failure('User nicht gefunden');

        const username = data.username.trim();
        if (!username) return failure('Benutzername ist erforderlich', undefined, { field: 'username' });

        // Validate username format (alphanumeric, dots, hyphens, underscores)
        if (!/^[a-zA-Z0-9._-]+$/.test(username)) {
            return failure('Benutzername darf nur Buchstaben, Zahlen, Punkte, Bindestriche und Unterstriche enthalten', undefined, { field: 'username' });
        }

        if (!data.firstName?.trim()) return failure('Vorname ist erforderlich', undefined, { field: 'firstName' });
        if (!data.lastName?.trim()) return failure('Nachname ist erforderlich', undefined, { field: 'lastName' });
        const name = buildDisplayName(data.firstName, data.lastName);

        if (username !== currentUser.username) {
            const existingUsername = await prisma.user.findUnique({ where: { username } });
            if (existingUsername) return failure('Benutzername wird bereits verwendet', undefined, { field: 'username' });
        }

        const normalizedEmail = data.email.trim().toLowerCase();
        const currentEmail = currentUser.email?.trim().toLowerCase() ?? '';
        const currentPendingEmail = currentUser.pendingEmail?.trim().toLowerCase() ?? null;

        let pendingEmail = currentUser.pendingEmail ?? null;
        let nextEmail = currentUser.email;
        let nextEmailVerified: Date | null | undefined = currentUser.emailVerified;
        let verificationTargetEmail: string | null = null;
        let pendingCleared = false;

        if (!currentUser.email && normalizedEmail) {
            const existing = await prisma.user.findFirst({
                where: {
                    OR: [
                        { email: normalizedEmail },
                        { pendingEmail: normalizedEmail },
                    ],
                },
            });
            if (existing) return failure('Email wird bereits verwendet', undefined, { field: 'email' });
            nextEmail = normalizedEmail;
            nextEmailVerified = null;
            pendingEmail = null;
            verificationTargetEmail = normalizedEmail;
        } else if (normalizedEmail && normalizedEmail !== currentEmail) {
            if (!currentPendingEmail || normalizedEmail !== currentPendingEmail) {
                const existing = await prisma.user.findFirst({
                    where: {
                        OR: [
                            { email: normalizedEmail },
                            { pendingEmail: normalizedEmail },
                        ],
                        NOT: { id: currentUser.id },
                    },
                });
                if (existing) return failure('Email wird bereits verwendet', undefined, { field: 'email' });
                pendingEmail = normalizedEmail;
                verificationTargetEmail = normalizedEmail;
            }
        } else if (currentPendingEmail) {
            pendingEmail = null;
            pendingCleared = true;
        }

        const updatedUser = await prisma.user.update({
            where: { id: currentUser.id },
            data: {
                name,
                firstName: data.firstName.trim(),
                lastName: data.lastName.trim(),
                email: nextEmail,
                pendingEmail,
                username: username,
                emailVerified: nextEmailVerified,
            }
        });

        if (pendingCleared) {
            await prisma.emailVerificationToken.deleteMany({ where: { userId: updatedUser.id } });
        }

        if (verificationTargetEmail) {
            const token = await createEmailVerificationToken(updatedUser.id);
            const baseUrl = process.env.PORTAL_PUBLIC_URL || process.env.NEXTAUTH_URL || '';
            const verificationUrl = `${baseUrl.replace(/\/$/, '')}/verify-email?token=${token}`;
            const emailResult = await sendEmail({
                to: verificationTargetEmail,
                subject: 'Bitte E-Mail-Adresse bestaetigen',
                text: `Bitte bestaetigen Sie Ihre E-Mail-Adresse: ${verificationUrl}`,
                html: `<p>Bitte bestaetigen Sie Ihre E-Mail-Adresse:</p><p><a href=\"${verificationUrl}\">${verificationUrl}</a></p>`,
            });
            if (emailResult.error) {
                return failure(emailResult.error);
            }
        }

        revalidatePath('/account');
        return ok({ emailVerified: updatedUser.emailVerified, pendingEmail: updatedUser.pendingEmail });
    } catch (e) {
        console.error(e);
        void safeLogSystemError('User profile update failed', e);
        return failure('Fehler beim Aktualisieren des Profils');
    }
}

export async function registerUser(data: {
    firstName: string;
    lastName: string;
    email: string;
    username?: string;
    password: string;
    recaptchaToken: string;
    inviteToken?: string;
}): Promise<ActionResult> {
    try {
        // Code-review S-A (2026-05-13): per-IP rate limit on top of
        // reCAPTCHA — reCAPTCHA can be solved at scale by paid services
        // and doesn't bound the per-host request rate.
        const ip = await getRequestIp();
        const { success: ipOk } = await registerIpLimiter.limit(`register-ip:${ip}`);
        if (!ipOk) {
            // Same generic German message as the duplicate-email path
            // below — don't expose whether the rate-limit was hit.
            return failure('Registrierung konnte nicht abgeschlossen werden. Bitte versuchen Sie es später erneut.');
        }

        const email = data.email.trim().toLowerCase();
        const name = buildDisplayName(data.firstName, data.lastName);
        const baseUsername = buildUsernameBase(data.firstName, data.lastName);
        const usernameInput = data.username?.trim() ?? '';
        const username = usernameInput || await findAvailableUsername(baseUsername);

        if (!email || !username || !data.password || !name) {
            return failure('Bitte alle Pflichtfelder ausfüllen');
        }

        if (data.password.length < MIN_PASSWORD_LENGTH) {
            return failure(`Passwort muss mindestens ${MIN_PASSWORD_LENGTH} Zeichen lang sein.`);
        }

        const recaptcha = await verifyRecaptcha(data.recaptchaToken);
        if (!recaptcha.success) {
            return failure(recaptcha.error || 'reCAPTCHA fehlgeschlagen');
        }

        if (!isSmtpConfigured()) {
            const missing = getMissingSmtpConfig();
            const missingHint = missing.length > 0 ? ` (fehlt: ${missing.join(', ')})` : '';
            return failure(`SMTP ist nicht konfiguriert${missingHint}`);
        }

        // Code-review S-A (2026-05-13): merge "email exists" and
        // "username taken" responses into a single generic message so
        // the action can't be used as a registered-email enumeration
        // oracle. The legitimate user knows they have an account; the
        // attacker can't tell email-taken from username-taken from
        // "you fat-fingered your firstName".
        const GENERIC_REGISTER_BLOCKED =
            'Diese Daten können nicht registriert werden. Falls Sie schon ein Konto haben, melden Sie sich an oder setzen Sie Ihr Passwort zurück.';

        const existingEmail = await prisma.user.findUnique({ where: { email } });
        if (existingEmail) {
            // Check if this is a placeholder user (invited but never registered)
            if (existingEmail.invitedAt && !existingEmail.password) {
                // Upgrade placeholder to real user — keep existing AccessBindings
            } else {
                return failure(GENERIC_REGISTER_BLOCKED);
            }
        }

        const existingUsername = await prisma.user.findUnique({ where: { username } });
        if (existingUsername && existingUsername.id !== existingEmail?.id) {
            return failure(GENERIC_REGISTER_BLOCKED);
        }

        const hashedPassword = await bcrypt.hash(data.password, SALT_ROUNDS);

        // Check for soft-deleted user with same email → reactivate
        const deletedUser = await prisma.user.findFirst({
            where: { deletedEmail: email, deletedAt: { not: null } },
        });

        let userId: number;

        if (existingEmail?.invitedAt && !existingEmail.password) {
            // Upgrade placeholder user to real account (keep existing AccessBindings)
            await prisma.user.update({
                where: { id: existingEmail.id },
                data: {
                    name,
                    firstName: data.firstName.trim(),
                    lastName: data.lastName.trim(),
                    username,
                    password: hashedPassword,
                    invitedAt: null,
                    emailVerified: null,
                    creationSource: 'self_registration',
                },
            });

            await logAuditEvent({
                userId: existingEmail.id,
                action: 'user.upgrade_placeholder',
                resourceType: 'User',
                resourceId: existingEmail.id,
                metadata: {
                    method: 'self_registration',
                    previousCreationSource: existingEmail.creationSource,
                },
            });

            userId = existingEmail.id;
        } else if (deletedUser) {
            // Reactivate: restore identity, wipe permissions, set new credentials
            await prisma.$transaction([
                prisma.user.update({
                    where: { id: deletedUser.id },
                    data: {
                        deletedAt: null,
                        deletedById: null,
                        email,
                        username,
                        deletedEmail: null,
                        deletedUsername: null,
                        password: hashedPassword,
                        name,
                        firstName: data.firstName.trim(),
                        lastName: data.lastName.trim(),
                        emailVerified: null,
                        creationSource: data.inviteToken ? 'invite' : 'self_registration',
                    },
                }),
                prisma.accessBinding.deleteMany({ where: { userId: deletedUser.id } }),
                prisma.groupMembership.deleteMany({ where: { userId: deletedUser.id } }),
                prisma.report.deleteMany({ where: { userId: deletedUser.id } }),
            ]);

            await logAuditEvent({
                userId: deletedUser.id,
                action: 'user.reactivate',
                resourceType: 'User',
                resourceId: deletedUser.id,
                metadata: {
                    method: 'self_registration',
                    previousEmail: deletedUser.deletedEmail,
                },
            });

            userId = deletedUser.id;
        } else {
            const newUser = await prisma.user.create({
                data: {
                    name,
                    firstName: data.firstName.trim(),
                    lastName: data.lastName.trim(),
                    email,
                    username,
                    password: hashedPassword,
                    emailVerified: null,
                    creationSource: data.inviteToken ? 'invite' : 'self_registration',
                }
            });
            userId = newUser.id;
        }

        // Consume invite token if present
        if (data.inviteToken) {
            try {
                const invite = await prisma.inviteToken.findFirst({
                    where: {
                        token: data.inviteToken,
                        email,
                        consumedAt: null,
                        expiresAt: { gt: new Date() },
                    },
                });
                if (invite) {
                    await prisma.inviteToken.update({
                        where: { id: invite.id },
                        data: { consumedAt: new Date() },
                    });

                    await prisma.user.update({
                        where: { id: userId },
                        data: {
                            createdById: invite.invitedById,
                        },
                    });

                    let parsedPendingRoles: PendingRoleInput[] = [];
                    try {
                        const rawPending = JSON.parse(invite.pendingRoles || '[]');
                        if (Array.isArray(rawPending)) {
                            parsedPendingRoles = rawPending
                                .filter((item): item is { roleSlug: string; networkId: number; plantMemberNr?: string } => {
                                    return typeof item?.roleSlug === 'string'
                                        && Number.isFinite(Number(item?.networkId));
                                })
                                .map((item) => ({
                                    roleSlug: item.roleSlug,
                                    networkId: Number(item.networkId),
                                    plantMemberNr: typeof item.plantMemberNr === 'string' ? item.plantMemberNr : undefined,
                                }));
                        }
                    } catch {
                        parsedPendingRoles = [];
                    }

                    const validatedPendingRoles = await validatePendingRoleAssignments({
                        actorUserId: invite.invitedById,
                        pendingRoles: parsedPendingRoles,
                        enforcedNetworkId: invite.networkId ?? undefined,
                        strict: false,
                    });
                    if (validatedPendingRoles.rejected.length > 0) {
                        logger.warning('Skipping invalid invite pending roles', { rejected: validatedPendingRoles.rejected }, 'Register');
                    }

                    for (const pending of validatedPendingRoles.roles) {
                        await prisma.accessBinding.create({
                            data: {
                                userId,
                                roleId: pending.roleId,
                                networkId: pending.networkId,
                                plantMemberNr: pending.plantMemberNr,
                                createdById: invite.invitedById,
                                notes: 'Auto-assigned from invitation',
                            },
                        });
                    }
                }
            } catch (e) {
                console.error('[Register] Failed to consume invite token:', e);
            }
        }

        const token = await createEmailVerificationToken(userId);
        const baseUrl = process.env.PORTAL_PUBLIC_URL || process.env.NEXTAUTH_URL || '';
        const verificationUrl = `${baseUrl.replace(/\/$/, '')}/verify-email?token=${token}`;

        const emailResult = await sendEmail({
            to: email,
            subject: 'Bitte E-Mail-Adresse bestaetigen',
            text: `Bitte bestaetigen Sie Ihre E-Mail-Adresse: ${verificationUrl}`,
            html: `<p>Bitte bestaetigen Sie Ihre E-Mail-Adresse:</p><p><a href=\"${verificationUrl}\">${verificationUrl}</a></p>`,
        });
        if (emailResult.error) {
            return failure(emailResult.error);
        }

        return ok();
    } catch (e) {
        console.error(e);
        void safeLogSystemError('User register failed', e, { email: data.email, username: data.username });
        return failure('Fehler bei der Registrierung');
    }
}

export async function disconnectAccount(accountId: number): Promise<ActionResult> {
    try {
        const userId = await getCurrentUserId();
        if (!userId) return failure('Nicht eingeloggt');

        // Verify account belongs to user
        const account = await prisma.account.findUnique({ where: { id: accountId } });
        if (!account || account.userId !== userId) {
            return failure('Account nicht gefunden');
        }

        // Check safety: Prevent deletion of last login method
        const user = await prisma.user.findUnique({
            where: { id: userId },
            include: { accounts: true }
        });

        if (!user) return failure('User nicht gefunden');

        const hasPassword = !!user.password;
        const accountCount = user.accounts.length;

        if (!hasPassword && accountCount <= 1) {
            return failure('Sie können nicht Ihre letzte Login-Methode entfernen. Bitte setzen Sie zuerst ein Passwort.');
        }

        await prisma.account.delete({ where: { id: accountId } });
        revalidatePath('/account');
        return ok();
    } catch (e) {
        console.error(e);
        void safeLogSystemError('Account disconnect failed', e, { accountId });
        return failure('Fehler beim Trennen der Verbindung');
    }
}
